import { useEffect, useId, useRef, useState } from 'react';

import ImageUploading from "react-images-uploading";
import Zoom from 'react-medium-image-zoom';
import 'react-medium-image-zoom/dist/styles.css';
import MarkdownEditor from '../components/MarkdownEditor';
import { getEntityType, MarketplaceOffer } from '@the-real-insight/tri-model';
import { userAPI } from '../UserAPI';
import { useModalContext } from '../components/ModalProvider';
import { useTranslation } from 'react-i18next';
import { useAtom } from 'jotai';
import { authenticationAtom } from '../State';

import { Camera, Trash2 } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';

/** Auswahl „keine Demo“ — Radix erlaubt keinen leeren Wert, der Bestand nutzte den leeren Schlüssel. */
const NO_DEMO_OFFER = '__none__';

interface DemoOfferOption {
    key: string;
    text: string;
}

interface MarketingDetailsProps {
    type: any
    marketplaceOffer: MarketplaceOffer;
    setMarketplaceOffer: (marketplaceOffer: MarketplaceOffer) => void;
    onImagesChange: (imageList: any, addUpdateIndex: any) => void;
}

export default function MarketingDetails({
    type,
    marketplaceOffer,
    setMarketplaceOffer,
    onImagesChange,
}: MarketingDetailsProps) {

    const { t } = useTranslation();
    const fieldId = useId();
    const [authentication] = useAtom(authenticationAtom) as any;
    const { pushError, showDeleteDialog: _showDeleteDialog } = useModalContext() as any;
    const [demoOffers, setDemoOffers] = useState<DemoOfferOption[]>([]);
    const [loadingDemoOffers, setLoadingDemoOffers] = useState(false);
    const termsAndConditionsURL = (marketplaceOffer as any).urlTermsAndConditions || (marketplaceOffer as any).termsAndConditionsURL || '';
    const privacyPolicyURL = (marketplaceOffer as any).urlPrivacyPolicy || (marketplaceOffer as any).privacyPolicyURL || '';

    const editorOptions = {
        fontSize: 12,
        minimap: { enabled: false },
        scrollbar: {
            vertical: 'hidden' as const,
            horizontal: 'hidden' as const
        },
        lineNumbers: 'off' as const,
        fontFamily: "monospace"
    };

    const containerRef = useRef<HTMLDivElement>(null);

    // Fix the parent container to take full width
    useEffect(() => {
        if (containerRef.current && containerRef.current.parentElement) {
            const parent = containerRef.current.parentElement;
            parent.style.width = '100%';
            parent.style.flex = '1';
            parent.style.minWidth = '0';
        }
    }, []);

    // Load demo offers (MCP Toolset, Knowledge Base, Prompt Tool, Agentic Workflow)
    useEffect(() => {
        const loadDemoOffers = async () => {
            if (!authentication?.customer) return;

            try {
                setLoadingDemoOffers(true);
                const offers = await userAPI.getMarketplaceOffers(authentication.customer);

                // Filter for published offers of the specified types
                const validTypes = ['AgentToolset', 'KnowledgeBase', 'PromptTool', 'AgenticWorkflow'];
                const filteredOffers = offers.filter((offer: any) =>
                    validTypes.includes(offer._type) &&
                    offer.published === true &&
                    offer._id !== marketplaceOffer._id // Exclude current offer
                );

                // Map to dropdown options
                const options: DemoOfferOption[] = [
                    { key: '', text: t('ui.pleaseSelect') || 'Please select...' }
                ];

                filteredOffers.forEach((offer: any) => {
                    const typeLabel = offer._type === 'AgentToolset' ? 'MCP Toolset' :
                                     offer._type === 'KnowledgeBase' ? 'Knowledge Base' :
                                     offer._type === 'PromptTool' ? 'Prompt Tool' :
                                     offer._type === 'AgenticWorkflow' ? 'Agentic Workflow' : offer._type;
                    options.push({
                        key: offer._id,
                        text: `${offer.name} (${typeLabel})`
                    });
                });

                setDemoOffers(options);
            } catch (error: any) {
                pushError(error);
            } finally {
                setLoadingDemoOffers(false);
            }
        };

        loadDemoOffers();
    }, [authentication?.customer, marketplaceOffer._id, pushError, t]);

    return (
        <div ref={containerRef} className="flex w-full min-w-0 flex-1 flex-col">
            {/* Row 1: Synopsis and Images */}
            <div className="mb-4 grid w-full min-w-0 gap-4 md:grid-cols-2">
                <div className="min-w-0">
                    <div className="mb-2 text-base font-semibold">{t('ui.synopsis')}</div>
                    <MarkdownEditor
                        value={marketplaceOffer.synopsis}
                        onChange={(value) => {
                            setMarketplaceOffer({
                                ...(marketplaceOffer as any),
                                synopsis: value
                            } as any);
                        }}
                        height="180px"
                        width="100%"
                        editorOptions={editorOptions}
                        placeholder={t('ui.synopsisPlaceholder')}
                    />
                </div>
                <div className="min-w-0">
                    <div className="mb-4 text-base font-semibold">{t('ui.marketplaceImages')}</div>
                    <ImageUploading
                        value={marketplaceOffer.images}
                        onChange={onImagesChange}
                        maxNumber={3}
                        maxFileSize={8000000}
                    >
                        {({
                            imageList: _imageList,
                            onImageUpload,
                            onImageRemoveAll: _onImageRemoveAll,
                            onImageUpdate: _onImageUpdate,
                            onImageRemove: _onImageRemove,
                            isDragging,
                            dragProps,
                        }) => (
                            <div className="flex flex-wrap gap-4">
                                <div
                                    className={cn(
                                        'flex h-[100px] w-[150px] flex-col items-center justify-center rounded-md border border-dashed bg-accent',
                                        isDragging && 'border-destructive'
                                    )}
                                    {...dragProps}>
                                    <Button type="button" variant="ghost" onClick={onImageUpload}>
                                        <Camera className="mr-2 h-4 w-4" aria-hidden />
                                        {t('ui.uploadFile')}
                                    </Button>
                                </div>
                                {marketplaceOffer.images ? marketplaceOffer.images.map((uri: string, index: number) => (
                                    <div key={index} className="flex flex-col items-center justify-center">
                                        <Zoom>
                                            <div
                                                role="img"
                                                aria-label={t('ui.marketplaceImages')}
                                                className="h-[100px] w-[150px] rounded-md border bg-cover bg-no-repeat"
                                                style={{ backgroundImage: `url(${userAPI.getResourceURL(uri)})` }} />
                                        </Zoom>
                                        <Button type="button" variant="ghost" onClick={async () => {
                                            try {
                                                const entityTypeName = marketplaceOffer?._type || type?.name;
                                                if (entityTypeName) {
                                                    const entityType = getEntityType(entityTypeName);
                                                    if (entityType?.type?.type) {
                                                        const updatedMarketplaceOffer = new (entityType.type.type)({ ...marketplaceOffer });
                                                        updatedMarketplaceOffer.images = updatedMarketplaceOffer.images.filter((_: any, i: number) => i !== index);
                                                        const result = await userAPI.updateMarketplaceOffer(authentication.customer, updatedMarketplaceOffer);
                                                        if (result) {
                                                            setMarketplaceOffer(result);
                                                        }
                                                    } else {
                                                        const updatedMarketplaceOffer = { ...marketplaceOffer };
                                                        updatedMarketplaceOffer.images = updatedMarketplaceOffer.images.filter((_: any, i: number) => i !== index);
                                                        setMarketplaceOffer(updatedMarketplaceOffer as any);
                                                    }
                                                }
                                            } catch (error: any) {
                                                pushError(error);
                                            }
                                        }}>
                                            <Trash2 className="mr-2 h-4 w-4" aria-hidden />
                                            Delete
                                        </Button>
                                    </div>
                                )) : <></>}
                            </div>
                        )}
                    </ImageUploading>
                </div>
            </div>

            {/* Row 2: Marketing Description and URL Fields */}
            <div className="grid w-full min-w-0 gap-4 md:grid-cols-2">
                <div className="min-w-0">
                    <div className="mb-2 text-base font-semibold">{t('ui.marketingTextHeading')}</div>
                    <div className="mb-2 text-sm">{t('ui.marketingTextInfo')}</div>
                    <MarkdownEditor
                        value={marketplaceOffer.marketingContent}
                        onChange={(value) => {
                            setMarketplaceOffer({
                                ...(marketplaceOffer as any),
                                marketingContent: value
                            } as any);
                        }}
                        height="300px"
                        width="100%"
                        editorOptions={editorOptions}
                        placeholder={t('ui.marketingContentPlaceholder')}
                    />
                </div>
                <div className="min-w-0">
                    <div className="mb-4 flex flex-col gap-1.5">
                        <Label htmlFor={`${fieldId}-demoOffer`}>{t('ui.demoOffer')}</Label>
                        <Select
                            value={(marketplaceOffer as any).demoOffer || NO_DEMO_OFFER}
                            onValueChange={(value) => {
                                setMarketplaceOffer({
                                    ...(marketplaceOffer as any),
                                    demoOffer: value === NO_DEMO_OFFER ? undefined : value
                                } as any);
                            }}
                            disabled={loadingDemoOffers || (marketplaceOffer as any).demoDisabled === true}
                        >
                            <SelectTrigger id={`${fieldId}-demoOffer`} className="w-full">
                                <SelectValue placeholder={t('ui.pleaseSelect') || 'Please select...'} />
                            </SelectTrigger>
                            <SelectContent>
                                {demoOffers.map((option) => (
                                    <SelectItem key={String(option.key)} value={String(option.key) || NO_DEMO_OFFER}>
                                        {option.text}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <div className="text-sm text-muted-foreground">{t('ui.demoOfferInfo')}</div>
                    </div>
                    <div className="mb-4">
                        <div className="flex items-center gap-2">
                            <Checkbox
                                id={`${fieldId}-demoDisabled`}
                                checked={(marketplaceOffer as any).demoDisabled === true}
                                onCheckedChange={(checked) => {
                                    setMarketplaceOffer({
                                        ...(marketplaceOffer as any),
                                        demoDisabled: checked === true
                                    } as any);
                                }}
                            />
                            <Label htmlFor={`${fieldId}-demoDisabled`}>{t('ui.demoDisabled')}</Label>
                        </div>
                        <div className="mt-3 text-sm text-muted-foreground">{t('ui.demoDisabledInfo')}</div>
                    </div>
                    <div className="mb-4 flex flex-col gap-1.5">
                        <Label htmlFor={`${fieldId}-contactEmail`}>{t('ui.contactEmail')}</Label>
                        <Input
                            id={`${fieldId}-contactEmail`}
                            type="email"
                            className="w-full"
                            value={(marketplaceOffer as any).contactEmail || ''}
                            placeholder={t('ui.marketplaceSubmissionPlaceholder')}
                            onChange={(event) => {
                                setMarketplaceOffer({
                                    ...(marketplaceOffer as any),
                                    contactEmail: event.target.value
                                } as any);
                            }}
                        />
                    </div>
                    <div className="mb-2 text-base font-semibold">{t('ui.urls')}</div>
                    <div className="mb-4 flex flex-col gap-1.5">
                        <Label htmlFor={`${fieldId}-terms`}>{t('ui.urlTermsAndConditions')}</Label>
                        <Input
                            id={`${fieldId}-terms`}
                            className="w-full"
                            value={termsAndConditionsURL}
                            placeholder={t('ui.marketplaceSubmissionPlaceholder')}
                            onChange={(event) => {
                                setMarketplaceOffer({
                                    ...(marketplaceOffer as any),
                                    urlTermsAndConditions: event.target.value,
                                    termsAndConditionsURL: event.target.value
                                } as any);
                            }}
                        />
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <Label htmlFor={`${fieldId}-privacy`}>{t('ui.urlPrivacyPolicy')}</Label>
                        <Input
                            id={`${fieldId}-privacy`}
                            className="w-full"
                            value={privacyPolicyURL}
                            placeholder={t('ui.marketplaceSubmissionPlaceholder')}
                            onChange={(event) => {
                                setMarketplaceOffer({
                                    ...(marketplaceOffer as any),
                                    urlPrivacyPolicy: event.target.value,
                                    privacyPolicyURL: event.target.value
                                } as any);
                            }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
